import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  ButtonBase,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StarIcon from '@mui/icons-material/Star';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AppDialog from '../components/AppDialog';
import { useModalHistory } from '../utils/useModalHistory';
import { sitePalette } from '../features/planning/colors';
import { PRICING_TIERS } from '../features/landing/pricing';
import { LANDING_CONTACT_EMAIL } from '../features/landing/constants';
import { PRIMARY_LIGHT } from '../lib/theme';
import {
  APP_LABELS,
  LANDING_LABELS as LABELS,
  REGISTER_DIALOG_LABELS as REGISTER_LABELS,
} from '../utils/labels';

// Reveals a mockup's entrance animation whenever it scrolls into view, rather
// than once on mount — most of these sit below the fold, so an on-mount
// trigger would already have finished playing before the visitor ever
// scrolls to see it. Toggles back out when the element leaves the viewport
// so the animation replays each time the visitor scrolls back to it.
// Falls back to "always in view" when IntersectionObserver is unavailable
// (jsdom in tests) so nothing gets stuck invisible.
function useScrollReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, inView };
}

interface LandingHeaderProps {
  onRegisterClick: () => void;
}

function LandingHeader({ onRegisterClick }: LandingHeaderProps) {
  const navigate = useNavigate();
  const { mode, setMode } = useColorScheme();

  useEffect(() => {
    if (!mode || mode === 'system') {
      setMode(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    }
  }, []);

  const isDark = mode === 'dark';
  const themeToggleLabel = isDark ? LABELS.themeLightLabel : LABELS.themeDarkLabel;

  return (
    <header className="relative overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/25 blur-3xl" />
        <div className="absolute top-1/3 -right-32 h-80 w-80 rounded-full bg-primary/15 blur-3xl" />
      </div>

      <div className="relative flex items-center gap-3 px-4 py-2.5 max-w-6xl mx-auto">
        <ButtonBase onClick={() => navigate('/')} aria-label={APP_LABELS.appName} className="rounded">
          <img src="/planly-logo-cropped.svg" alt={APP_LABELS.appName} className="h-9 w-auto md:hidden" />
          <img src="/planly-logo-light.svg" alt={APP_LABELS.appName} className="h-9 w-auto hidden md:block dark:hidden" />
          <img src="/planly-logo-dark.svg" alt={APP_LABELS.appName} className="h-9 w-auto hidden md:dark:block" />
        </ButtonBase>

        <div className="ml-auto flex items-center gap-2">
          <Tooltip title={themeToggleLabel}>
            <IconButton onClick={() => setMode(isDark ? 'light' : 'dark')} aria-label={themeToggleLabel} color="inherit">
              {isDark ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </IconButton>
          </Tooltip>
          <Button variant="outlined" color="inherit" onClick={onRegisterClick} className="hidden sm:inline-flex">
            {LABELS.navDemoButton}
          </Button>
          <Button variant="contained" onClick={() => navigate('/login')} data-testid="landing-login-button">
            {LABELS.navLogin}
          </Button>
        </div>
      </div>

      <div className="relative px-4 pt-8 pb-16 md:pt-12 md:pb-24">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight mb-6">
            <span className="block">{LABELS.heroTitleMain}</span>
            <span className="block bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent">
              {LABELS.heroTitleAccent}
            </span>
          </h1>

          <p className="text-lg text-text-secondary mb-8 max-w-md mx-auto">{LABELS.heroSubtitle}</p>

          <div className="flex flex-col items-center gap-2">
            <Button variant="contained" size="large" onClick={onRegisterClick}>
              {LABELS.heroTrialButton}
            </Button>
            <Button variant="text" component="a" href="#pricing" className="underline">
              {LABELS.heroPricingLink}
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}

interface PlanningPreviewRow {
  name: string;
  offset: number;
  width: number;
}

const PLANNING_PREVIEW_ROWS: PlanningPreviewRow[] = LABELS.heroMockupMachines.map((name, i) => ({
  name,
  offset: [8, 22, 4, 38, 15][i] ?? 10,
  width: [46, 34, 58, 30, 40][i] ?? 40,
}));

const PLANNING_PREVIEW_TODAY_POSITION = 40;

function PlanningPreview() {
  const { ref, inView } = useScrollReveal<HTMLDivElement>();

  return (
    <div ref={ref} aria-hidden="true" className="rounded-3xl border border-border bg-surface shadow-2xl shadow-primary/10 overflow-hidden">
      <div className="relative px-5 py-5 flex flex-col gap-3">
        {PLANNING_PREVIEW_ROWS.map((row, i) => (
          <div key={row.name} className="flex items-center gap-3">
            <span className="w-24 shrink-0 truncate text-xs text-text-secondary">{row.name}</span>
            <div className="relative h-6 flex-1 rounded-md bg-background">
              <div
                className="absolute inset-y-0 rounded-md transition-[width] duration-700 ease-out motion-reduce:transition-none"
                style={{
                  left: `${row.offset}%`,
                  width: inView ? `${row.width}%` : '0%',
                  backgroundColor: sitePalette[i % sitePalette.length],
                  transitionDelay: `${i * 90}ms`,
                }}
              />
            </div>
          </div>
        ))}

        <div
          className="pointer-events-none absolute top-0 bottom-0 w-0.5"
          style={{
            left: `${28 + PLANNING_PREVIEW_TODAY_POSITION * 0.55}%`,
            backgroundImage:
              'repeating-linear-gradient(to bottom, var(--color-primary) 0, var(--color-primary) 4px, transparent 4px, transparent 8px)',
          }}
        />
      </div>
    </div>
  );
}

interface DeepDiveBlockProps {
  eyebrow?: string;
  badge?: string;
  title: string;
  body: string;
  bullets: readonly string[];
  visual: ReactNode;
  imagePosition?: 'left' | 'right';
  tone?: 'default' | 'surface';
}

function DeepDiveBlock({ eyebrow, badge, title, body, bullets, visual, imagePosition = 'right', tone = 'default' }: DeepDiveBlockProps) {
  const textOrder = imagePosition === 'left' ? 'md:order-2' : 'md:order-1';
  const visualOrder = imagePosition === 'left' ? 'md:order-1' : 'md:order-2';

  return (
    <section className={`px-4 py-20 ${tone === 'surface' ? 'bg-surface border-y border-border' : ''}`}>
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
        <div className={textOrder}>
          {badge && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/30 px-3 py-1.5 text-sm font-semibold uppercase tracking-wide text-primary mb-4">
              <StarIcon aria-hidden="true" sx={{ fontSize: '0.9rem' }} />
              {badge}
            </span>
          )}
          {!badge && eyebrow && (
            <span className="block text-base uppercase tracking-widest text-primary mb-3">{eyebrow}</span>
          )}
          <h2 className="text-3xl font-bold mb-3">{title}</h2>
          <p className="text-text-secondary mb-6">{body}</p>
          <ul className="flex flex-col gap-2 mb-4 pl-0">
            {bullets.map((bullet) => (
              <li key={bullet} className="flex items-start gap-2 text-base text-text-secondary">
                <CheckCircleOutlineIcon aria-hidden="true" className="text-primary shrink-0 mt-0.5" sx={{ fontSize: '1.1rem' }} />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={visualOrder}>{visual}</div>
      </div>
    </section>
  );
}

function SmartPlannerMockup() {
  const requestTitles = LABELS.smartPlannerMockupRequestTitles;
  const requestDates = LABELS.smartPlannerMockupRequestDates;
  const { ref, inView } = useScrollReveal<HTMLDivElement>();

  return (
    <div ref={ref} aria-hidden="true" className="flex items-center gap-3 flex-wrap">
      <div className="flex-1 rounded-2xl border border-border bg-surface shadow-xl shadow-primary/10 p-4 flex flex-col gap-2">
        {requestTitles.map((title, i) => (
          <div key={title} className="rounded-lg bg-background px-3 py-2">
            <p className="text-sm font-semibold mb-0">{title}</p>
            <p className="text-xs text-text-secondary mt-1">{requestDates[i]}</p>
          </div>
        ))}
      </div>

      <ArrowForwardIcon aria-hidden="true" className="text-text-disabled shrink-0" />

      <div className="flex-1 flex flex-col gap-3">
        <div
          className={`rounded-2xl border border-border bg-surface shadow-xl shadow-primary/10 p-4 transition-all duration-700 ease-out motion-reduce:transition-none ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          }`}
          style={{ transitionDelay: '150ms' }}
        >
          <p className="text-sm uppercase tracking-widest mb-0 mt-0">
            {LABELS.smartPlannerMockupSuggestionLabel}
          </p>
          <p className="text-xs text-text-secondary mt-4 mb-0 mt-1">
            {LABELS.smartPlannerMockupSuggestionSubtitle}
          </p>
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-center text-xs font-medium mb-0 mt-4 text-emerald-600 dark:text-emerald-400">
            {LABELS.smartPlannerMockupSuggestionRange}
          </div>
        </div>
        <div
          className={`rounded-2xl border border-border bg-surface shadow-xl shadow-primary/10 p-4 transition-all duration-700 ease-out motion-reduce:transition-none ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          }`}
          style={{ transitionDelay: '400ms' }}
        >
          <p className="text-sm uppercase tracking-widest text-text-secondary mb-0 mt-0">
            {LABELS.smartPlannerMockupSavingsLabel}
          </p>
          <p
            className={`text-2xl font-bold text-emerald-600 dark:text-emerald-400 mb-0 mt-4 transition-all duration-500 ease-out motion-reduce:transition-none ${
              inView ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
            }`}
            style={{ transitionDelay: '600ms' }}
          >
            {LABELS.smartPlannerMockupSavingsValue}
          </p>
          <p className="text-xs text-text-secondary mt-4 mb-0">
            {LABELS.smartPlannerMockupSavingsCaption}
          </p>
        </div>
      </div>
    </div>
  );
}

function DashboardMockup() {
  const tileValues = LABELS.dashboardMockupTileValues;
  const tileLabels = LABELS.dashboardMockupTileLabels;
  const donutColors = [sitePalette[7], sitePalette[1], sitePalette[2], sitePalette[6]];
  const donutValues = [35, 30, 20, 15];
  let cumulative = 0;
  const stops = donutValues
    .map((v, i) => {
      const start = cumulative;
      cumulative += v;
      return `${donutColors[i]} ${start}% ${cumulative}%`;
    })
    .join(', ');
  const barHeights = ['70%', '100%', '40%'];
  const barColors = [sitePalette[7], sitePalette[4], sitePalette[9]];
  const { ref, inView } = useScrollReveal<HTMLDivElement>();

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="rounded-3xl border border-border bg-surface shadow-2xl shadow-primary/10 overflow-hidden p-5 flex flex-col gap-4"
    >
      <div className="grid grid-cols-4 gap-2 grow">
        {tileValues.map((value, i) => (
          <div
            key={tileLabels[i]}
            className="rounded-lg border border-border bg-background px-2 py-2 text-center"
          >
            <p className="text-lg font-bold leading-none">{value}</p>
            <p className="text-[0.55rem] text-text-secondary mt-1 truncate">{tileLabels[i]}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-6">
        <div className="grow flex items-center justify-center">
          <div
            className={`relative h-20 w-20 shrink-0 rounded-full transition-all duration-700 ease-out motion-reduce:transition-none ${
              inView ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
            }`}
            style={{ backgroundImage: `conic-gradient(${stops})` }}
          >
            <div className="absolute inset-2.5 rounded-full bg-surface" />
          </div>
        </div>
        <div className="flex items-end gap-2 h-20 grow justify-center">
          {barHeights.map((height, i) => (
            <div
              key={height}
              className="w-4 rounded-t transition-[height] duration-700 ease-out motion-reduce:transition-none"
              style={{
                height: inView ? height : '0%',
                backgroundColor: barColors[i],
                transitionDelay: `${150 + i * 120}ms`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <DashboardTimelineChart />
      </div>
    </div>
  );
}

function DashboardTimelineChart() {
  const points = [10, 45, 80, 115, 150, 185, 220, 255];
  const returnsY = [70, 55, 30, 55, 68, 70, 70, 70];
  const arrivalsY = [70, 70, 70, 70, 50, 65, 70, 70];
  const returnsLine = points.map((x, i) => `${x},${returnsY[i]}`).join(' ');
  const arrivalsLine = points.map((x, i) => `${x},${arrivalsY[i]}`).join(' ');
  const returnsColor = '#10b981';
  const arrivalsColor = '#a78bfa';

  return (
    <div>
      <svg viewBox="0 0 270 80" className="w-full h-auto">
        {[0, 1, 2, 3].map((i) => (
          <line key={i} x1="0" y1={10 + i * 20} x2="270" y2={10 + i * 20} className="text-border" stroke="currentColor" strokeDasharray="3 3" strokeWidth="1" />
        ))}
        <polyline points={returnsLine} fill="none" stroke={returnsColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points={arrivalsLine} fill="none" stroke={arrivalsColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((x, i) => (
          <circle key={`r-${x}`} cx={x} cy={returnsY[i]} r="2.5" fill={returnsColor} />
        ))}
        {points.map((x, i) => (
          <circle key={`a-${x}`} cx={x} cy={arrivalsY[i]} r="2.5" fill={arrivalsColor} />
        ))}
      </svg>
    </div>
  );
}

function PickupHandinMockup() {
  const { ref, inView } = useScrollReveal<HTMLDivElement>();
  const steps = LABELS.pickupMockupSteps;

  const nodeClass = (filled: boolean) =>
    `flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all duration-500 ease-out motion-reduce:transition-none ${
      filled ? 'bg-emerald-500 text-white scale-100' : 'bg-background border-2 border-border text-text-disabled scale-90'
    }`;

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="rounded-2xl border border-border bg-surface shadow-xl shadow-primary/10 p-6"
    >
      <div className="flex items-start">
        <div className="flex flex-col items-center gap-2">
          <div className={nodeClass(inView)}>
            <EventNoteOutlinedIcon aria-hidden="true" sx={{ fontSize: '1.1rem' }} />
          </div>
          <span className="text-xs font-semibold whitespace-nowrap">
            {steps[0]}
          </span>
        </div>
        <div className="relative mx-1 mt-[1.0625rem] h-0.5 flex-1 rounded-full bg-border">
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-emerald-500 transition-[width] duration-500 ease-out motion-reduce:transition-none"
            style={{ width: inView ? '100%' : '0%', transitionDelay: '150ms' }}
          />
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className={nodeClass(inView)} style={{ transitionDelay: '550ms' }}>
            <SwapHorizOutlinedIcon aria-hidden="true" sx={{ fontSize: '1.1rem' }} />
          </div>
          <span className="text-xs font-semibold whitespace-nowrap">
            {steps[1]}
          </span>
        </div>
        <div className="relative mx-1 mt-[1.0625rem] h-0.5 flex-1 rounded-full bg-border">
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-emerald-500 transition-[width] duration-500 ease-out motion-reduce:transition-none"
            style={{ width: inView ? '100%' : '0%', transitionDelay: '800ms' }}
          />
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className={nodeClass(inView)} style={{ transitionDelay: '1200ms' }}>
            <CheckCircleIcon aria-hidden="true" sx={{ fontSize: '1.1rem' }} />
          </div>
          <span className="text-xs font-semibold whitespace-nowrap">
            {steps[2]}
          </span>
        </div>
      </div>

      <div className="mx-auto mt-5 flex w-fit flex-col gap-2">
        <div
          className={`flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 transition-all duration-500 ease-out motion-reduce:transition-none ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
          }`}
          style={{ transitionDelay: '700ms' }}
        >
          <PersonOutlineIcon
            aria-hidden="true"
            className="text-primary shrink-0"
            sx={{ fontSize: '1.1rem' }}
          />
          <div className="min-w-0">
            <p className="mb-0 mt-0 text-xs uppercase tracking-widest text-text-secondary">
              {LABELS.pickupMockupPickedUpByLabel}
            </p>
            <p className="mb-0 mt-0 truncate text-sm font-semibold">
              {LABELS.pickupMockupName}
            </p>
          </div>
        </div>
        <div
          className={`flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 transition-all duration-500 ease-out motion-reduce:transition-none ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
          }`}
          style={{ transitionDelay: '1700ms' }}
        >
          <PersonOutlineIcon
            aria-hidden="true"
            className="text-primary shrink-0"
            sx={{ fontSize: '1.1rem' }}
          />
          <div className="min-w-0">
            <p className="mb-0 mt-0 text-xs uppercase tracking-widest text-text-secondary">
              {LABELS.pickupMockupHandedInByLabel}
            </p>
            <p className="mb-0 mt-0 truncate text-sm font-semibold">
              {LABELS.pickupMockupHandedInName}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

interface FeatureGridItem {
  icon: ReactNode;
  title: string;
  body: string;
  color: string;
}

const FEATURE_GRID_ICON_SX = { fontSize: '1.15rem' };

const FEATURE_GRID_ITEMS: FeatureGridItem[] = [
  { icon: <CategoryOutlinedIcon aria-hidden="true" className="text-white" sx={FEATURE_GRID_ICON_SX} />, title: LABELS.featureAssetsTitle, body: LABELS.featureAssetsBody, color: sitePalette[7] },
  { icon: <StorefrontOutlinedIcon aria-hidden="true" className="text-white" sx={FEATURE_GRID_ICON_SX} />, title: LABELS.featureSuppliersTitle, body: LABELS.featureSuppliersBody, color: sitePalette[10] },
  { icon: <PlaceOutlinedIcon aria-hidden="true" className="text-white" sx={FEATURE_GRID_ICON_SX} />, title: LABELS.featureProjectsTitle, body: LABELS.featureProjectsBody, color: sitePalette[4] },
  { icon: <EmailOutlinedIcon aria-hidden="true" className="text-white" sx={FEATURE_GRID_ICON_SX} />, title: LABELS.featureEmailsTitle, body: LABELS.featureEmailsBody, color: sitePalette[8] },
  { icon: <SettingsOutlinedIcon aria-hidden="true" className="text-white" sx={FEATURE_GRID_ICON_SX} />, title: LABELS.featureConfigTitle, body: LABELS.featureConfigBody, color: sitePalette[1] },
  { icon: <ManageAccountsOutlinedIcon aria-hidden="true" className="text-white" sx={FEATURE_GRID_ICON_SX} />, title: LABELS.featureUsersTitle, body: LABELS.featureUsersBody, color: sitePalette[6] },
  { icon: <InsightsOutlinedIcon aria-hidden="true" className="text-white" sx={FEATURE_GRID_ICON_SX} />, title: LABELS.featureReportsTitle, body: LABELS.featureReportsBody, color: sitePalette[5] },
  { icon: <ArchiveOutlinedIcon aria-hidden="true" className="text-white" sx={FEATURE_GRID_ICON_SX} />, title: LABELS.featureArchiveTitle, body: LABELS.featureArchiveBody, color: sitePalette[0] },
];

function FeatureGrid() {
  return (
    <section className="px-4 py-14 bg-surface border-y border-border">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <span className="text-base uppercase tracking-widest text-primary">
            {LABELS.featuresEyebrow}
          </span>
          <h2 className="text-3xl font-bold mt-2 mb-3">
            {LABELS.featuresTitleMain} <span className="text-primary">{LABELS.featuresTitleAccent}</span>
          </h2>
          <p className="text-text-secondary">{LABELS.featuresSubtitle}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURE_GRID_ITEMS.map((feature) => (
            <div
              key={feature.title}
              className="group flex flex-col gap-2 p-4 rounded-xl border border-border bg-background transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10 hover:border-primary/40 motion-reduce:hover:translate-y-0"
            >
              <div
                className="flex h-9 w-9 items-center justify-center rounded-lg transition-transform duration-300 group-hover:scale-110"
                style={{ backgroundColor: feature.color }}
              >
                {feature.icon}
              </div>
              <h3 className="font-semibold text-base mb-0 ">{feature.title}</h3>
              <p className="text-base text-text-secondary mb-0 mt-0">{feature.body}</p>
            </div>
          ))}
          <ButtonBase
            component="a"
            href="/help"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col items-start gap-2 p-4 rounded-xl border border-border bg-background text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10 hover:border-primary/40 motion-reduce:hover:translate-y-0"
          >
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg transition-transform duration-300 group-hover:scale-110"
              style={{ backgroundColor: sitePalette[9] }}
            >
              <MenuBookOutlinedIcon aria-hidden="true" className="text-white" sx={FEATURE_GRID_ICON_SX} />
            </div>
            <h3 className="font-semibold text-base mb-0">{LABELS.featureFaqTitle}</h3>
            <p className="text-base text-text-secondary mb-0 mt-0">{LABELS.featureFaqBody}</p>
            <span className="flex items-center gap-1 text-base font-semibold text-primary mt-1">
              {LABELS.featureFaqLinkLabel}
              <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: '1.1rem' }} />
            </span>
          </ButtonBase>
        </div>
      </div>
    </section>
  );
}

function ForWhoBlock() {
  return (
    <section className="px-4 py-20">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl font-bold mb-3">{LABELS.forWhoTitle}</h2>
        <p className="text-text-secondary mb-8">{LABELS.forWhoBody}</p>
        <ul className="flex flex-col gap-3 w-fit mx-auto text-left">
          {LABELS.forWhoItems.map((item) => (
            <li key={item} className="flex items-center gap-2">
              <CheckCircleOutlineIcon aria-hidden="true" className="text-primary shrink-0" sx={{ fontSize: '1.25rem' }} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

interface PricingBlockProps {
  onSelectTier: (tierName: string) => void;
}

function PricingBlock({ onSelectTier }: PricingBlockProps) {
  return (
    <section id="pricing" className="px-4 py-20 bg-surface border-y border-border">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-2">{LABELS.pricingTitle}</h2>
        <p className="text-base text-text-secondary text-center mb-12">{LABELS.pricingTrialNote}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PRICING_TIERS.map((tier) => (
            <div
              key={tier.id}
              className={`relative flex flex-col rounded-2xl border p-6 bg-background ${tier.badge ? 'border-primary shadow-lg shadow-primary/10' : 'border-border'}`}
            >
              {tier.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary px-3 py-1 text-sm font-semibold text-on-primary">
                  <StarIcon aria-hidden="true" sx={{ fontSize: '0.8rem' }} />
                  {tier.badge}
                </span>
              )}
              <h3 className="text-lg font-semibold mt-2">{tier.name}</h3>
              <p className="mb-2 mt-0">
                <span className="text-2xl font-bold">{tier.price}</span>{' '}
                <span className="text-sm text-text-secondary">{tier.period}</span>
              </p>
              <ul className="flex flex-col gap-2 mb-6 flex-1 pl-0">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-base text-text-secondary">
                    <CheckCircleOutlineIcon aria-hidden="true" className="text-primary shrink-0 mt-0.5" sx={{ fontSize: '1.1rem' }} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant={tier.badge ? 'contained' : 'outlined'}
                color={tier.badge ? 'primary' : 'inherit'}
                onClick={() => onSelectTier(tier.name)}
                fullWidth
              >
                {tier.ctaLabel}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface Step {
  number: string;
  title: string;
  body: string;
}

const HOW_IT_WORKS_STEPS: Step[] = [
  { number: LABELS.step1Number, title: LABELS.step1Title, body: LABELS.step1Body },
  { number: LABELS.step2Number, title: LABELS.step2Title, body: LABELS.step2Body },
  { number: LABELS.step3Number, title: LABELS.step3Title, body: LABELS.step3Body },
];

function HowItWorks() {
  return (
    <section className="px-4 py-20">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-base uppercase tracking-widest text-primary">
            {LABELS.howItWorksEyebrow}
          </span>
          <h2 className="text-3xl font-bold mt-2 mb-3">
            {LABELS.howItWorksTitle}
          </h2>
          <p className="text-text-secondary">{LABELS.howItWorksSubtitle}</p>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10">
          <div
            aria-hidden="true"
            className="hidden md:block absolute top-6 left-[16.5%] right-[16.5%] border-t border-dashed border-border"
          />
          {HOW_IT_WORKS_STEPS.map((step) => (
            <div key={step.number} className="relative flex flex-col items-center text-center gap-3">
              <span className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border border-border bg-surface text-sm font-semibold text-primary">
                {step.number}
              </span>
              <h3 className="font-semibold">{step.title}</h3>
              <p className="text-base text-text-secondary max-w-xs">{step.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface LandingCtaProps {
  onRegisterClick: () => void;
}

// Fixed brand-blue panel — deliberately does not flip with dark mode (see
// theme.ts PRIMARY_LIGHT, 7.55:1+ with white ✓ AAA) so the white text below
// stays AAA regardless of app theme, unlike the mode-dependent --color-primary
// token whose dark-mode value is a light lavender unsuited to a dark gradient end.
const LANDING_CTA_GRADIENT_FROM = PRIMARY_LIGHT;
const LANDING_CTA_GRADIENT_TO = '#1a2db0'; // matches theme.ts light-mode palette.primary.dark

function LandingCta({ onRegisterClick }: LandingCtaProps) {
  return (
    <section className="px-4 py-20">
      <div
        className="max-w-5xl mx-auto rounded-3xl px-6 py-16 text-center text-white overflow-hidden relative"
        style={{ backgroundImage: `linear-gradient(to bottom right, ${LANDING_CTA_GRADIENT_FROM}, ${LANDING_CTA_GRADIENT_TO})` }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle,rgba(255,255,255,0.5)_1px,transparent_1px)] bg-[length:24px_24px] opacity-10"
        />
        <div className="relative">
          <h2 className="text-3xl font-bold mb-3">
            {LABELS.ctaTitle}
          </h2>
          <p className="opacity-90 mb-8 max-w-xl mx-auto">{LABELS.ctaSubtitle}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              variant="contained"
              size="large"
              onClick={onRegisterClick}
              sx={{ bgcolor: '#ffffff', color: LANDING_CTA_GRADIENT_FROM, '&:hover': { bgcolor: '#ffffff', opacity: 0.9 } }}
            >
              {LABELS.ctaRegisterButton}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer className="px-2 py-2 border-t border-border bg-surface">
      <div className="flex flex-col items-center gap-1 text-base text-text-secondary text-center">
        <Button
          variant="text"
          color="inherit"
          component="a"
          href="/help"
          target="_blank"
          rel="noopener noreferrer"
          className="normal-case text-sm text-text-secondary hover:underline"
        >
          {LABELS.footerFaqLink}
        </Button>
        <p>{LABELS.footerCopyright(new Date().getFullYear())}</p>
      </div>
    </footer>
  );
}

interface RegisterDialogProps {
  onClose: () => void;
  interest?: string;
}

const REGISTER_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UNKNOWN_PRICING_INTEREST = 'unknown';

function RegisterDialog({ onClose, interest }: RegisterDialogProps) {
  useModalHistory(onClose);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [pricingInterest, setPricingInterest] = useState(interest || UNKNOWN_PRICING_INTEREST);
  const [message, setMessage] = useState('');

  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [organizationError, setOrganizationError] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    let hasError = false;

    if (!name.trim()) {
      setNameError(REGISTER_LABELS.nameRequired);
      hasError = true;
    }
    if (!email.trim()) {
      setEmailError(REGISTER_LABELS.emailRequired);
      hasError = true;
    } else if (!REGISTER_EMAIL_PATTERN.test(email.trim())) {
      setEmailError(REGISTER_LABELS.emailInvalid);
      hasError = true;
    }
    if (!organization.trim()) {
      setOrganizationError(REGISTER_LABELS.organizationRequired);
      hasError = true;
    }
    if (hasError) return;

    const pricingInterestLabel =
      pricingInterest === UNKNOWN_PRICING_INTEREST ? REGISTER_LABELS.pricingInterestUnknown : pricingInterest;
    const subject = REGISTER_LABELS.mailSubject;
    const body = [
      `${REGISTER_LABELS.mailNameLine}: ${name.trim()}`,
      `${REGISTER_LABELS.mailEmailLine}: ${email.trim()}`,
      `${REGISTER_LABELS.mailOrganizationLine}: ${organization.trim()}`,
      `${REGISTER_LABELS.mailInterestLine}: ${pricingInterestLabel}`,
      ...(message.trim() ? ['', message.trim()] : []),
    ].join('\n');
    const href = `mailto:${LANDING_CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    const a = document.createElement('a');
    a.href = href;
    a.click();

    onClose();
  };

  return (
    <AppDialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={REGISTER_LABELS.dialogTitle}
      confirmLabel={REGISTER_LABELS.submitButton}
      confirmFormId="register-form"
      testId="register-dialog"
    >
      <form id="register-form" onSubmit={handleSubmit} noValidate>
        <DialogContent className="flex flex-col gap-4">
          <p className="text-base text-text-secondary mb-0 mt-0">{REGISTER_LABELS.intro}</p>
          <TextField
            label={REGISTER_LABELS.nameLabel}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            required
            fullWidth
            error={!!nameError}
            helperText={nameError || undefined}
          />
          <TextField
            label={REGISTER_LABELS.emailLabel}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError('');
            }}
            required
            fullWidth
            error={!!emailError}
            helperText={emailError || undefined}
          />
          <TextField
            label={REGISTER_LABELS.organizationLabel}
            value={organization}
            onChange={(e) => {
              setOrganization(e.target.value);
              if (organizationError) setOrganizationError('');
            }}
            required
            fullWidth
            error={!!organizationError}
            helperText={organizationError || undefined}
          />
          <FormControl fullWidth>
            <InputLabel>{REGISTER_LABELS.pricingInterestLabel}</InputLabel>
            <Select
              label={REGISTER_LABELS.pricingInterestLabel}
              value={pricingInterest}
              onChange={(e) => setPricingInterest(e.target.value)}
              inputProps={{ 'aria-label': REGISTER_LABELS.pricingInterestLabel }}
            >
              <MenuItem value={UNKNOWN_PRICING_INTEREST}>{REGISTER_LABELS.pricingInterestUnknown}</MenuItem>
              {PRICING_TIERS.map((tier) => (
                <MenuItem key={tier.id} value={tier.name}>
                  {tier.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={REGISTER_LABELS.messageLabel}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose} color="inherit" data-testid="cancel-button">
            {REGISTER_LABELS.cancel}
          </Button>
          <Button type="submit" variant="contained" data-testid="submit-button">
            {REGISTER_LABELS.submitButton}
          </Button>
        </DialogActions>
      </form>
    </AppDialog>
  );
}

export default function LandingPage() {
  const [registerInterest, setRegisterInterest] = useState<string | null>(null);
  const openRegister = () => setRegisterInterest('');

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <LandingHeader onRegisterClick={openRegister} />
      <main className="flex-1">
        <DeepDiveBlock
          eyebrow={LABELS.plannerEyebrow}
          title={LABELS.plannerTitle}
          body={LABELS.plannerBody}
          bullets={LABELS.plannerBullets}
          visual={<PlanningPreview />}
          imagePosition="right"
          tone="surface"
        />

        <DeepDiveBlock
          badge={LABELS.smartPlannerBadge}
          title={LABELS.smartPlannerTitle}
          body={LABELS.smartPlannerBody}
          bullets={LABELS.smartPlannerBullets}
          visual={<SmartPlannerMockup />}
          imagePosition="left"
        />

        <DeepDiveBlock
          eyebrow={LABELS.dashboardDeepDiveEyebrow}
          title={LABELS.dashboardDeepDiveTitle}
          body={LABELS.dashboardDeepDiveBody}
          bullets={LABELS.dashboardDeepDiveBullets}
          visual={<DashboardMockup />}
          imagePosition="right"
          tone="surface"
        />

        <DeepDiveBlock
          eyebrow={LABELS.pickupDeepDiveEyebrow}
          title={LABELS.pickupDeepDiveTitle}
          body={LABELS.pickupDeepDiveBody}
          bullets={LABELS.pickupDeepDiveBullets}
          visual={<PickupHandinMockup />}
          imagePosition="left"
        />

        <FeatureGrid />
        <ForWhoBlock />
        <PricingBlock onSelectTier={(name) => setRegisterInterest(name)} />
        <HowItWorks />
        <LandingCta onRegisterClick={openRegister} />
      </main>
      <LandingFooter />

      {registerInterest !== null && (
        <RegisterDialog interest={registerInterest || undefined} onClose={() => setRegisterInterest(null)} />
      )}
    </div>
  );
}
